/**
 * Pure view logic for the Marketing Orders table.
 *
 * Kept out of the component so it can be tested under the `node` jest
 * environment — the jsdom one in this repo is broken, so a render test would
 * not run at all.
 */

export interface ProjectionBasis {
  /** Units already counted toward the exec's monthly total before this order. */
  units_before: number;
  /** Their total once this order is included. */
  units_after: number;
}

/**
 * Which unit(s) of the exec's month this order's incentive was priced on, or
 * null when there is nothing meaningful to say.
 *
 * Slabs are cumulative, so two orders of one unit each can legitimately pay
 * different amounts — ₹2,000 for the exec's 5th unit and ₹4,000 for their 6th.
 * The amount alone looks arbitrary or wrong; this is the sentence that makes it
 * read as correct.
 *
 * Returns null for a CREDITED order: the API sends no basis once an incentive
 * is approved, because the figure is then what was actually paid rather than a
 * projection of what would be.
 */
export const incentiveBasisLabel = (
  basis?: ProjectionBasis | null,
): string | null => {
  if (!basis) return null;

  const { units_before: before, units_after: after } = basis;
  if (typeof before !== 'number' || typeof after !== 'number') return null;

  const first = before + 1;
  const last = after;
  // after <= before would mean the order covers no units at all. Nothing
  // truthful to render, so say nothing rather than print a broken range.
  if (last < first) return null;

  return first === last
    ? `unit ${first} this month`
    : `units ${first}–${last} this month`;
};

/**
 * Whether the incentive slab indicates the order is below the first slab threshold,
 * where incentives cannot be approved.
 */
export const isBelowFirstSlab = (slab?: string | null): boolean => {
  if (!slab) return false;
  return /below first slab/i.test(slab.trim());
};

/** The money buckets GET /marketing/orders reports for the filtered set. */
export interface CollectionSummary {
  cash_collected?: number;
  online_amount_collected?: number;
  coins_amount_collected?: number;
  coupons_amount_collected?: number;
  other_amount_collected?: number;
}

export interface CollectionLine {
  label: string;
  value: number;
}

/**
 * The lines under "Total Collected", in display order.
 *
 * The API splits collections five ways — cash, online, coins, coupons, and
 * `other` as the complement of all of them. It used to report only cash and
 * online and call their sum the total, which silently dropped every other
 * method: on live that hid ₹128,500 of COINS_REDEEM in one September, and the
 * orders screen disagreed with the leaderboard about revenue for the same
 * orders.
 *
 * Online and cash always render, even at zero: they are the primary rails and
 * a manager reading a collections total expects both accounted for. The other
 * three render only when they carry money, because marketing-scope orders
 * mostly have no coin or coupon value and three permanent ₹0 lines would be
 * noise on every screen.
 *
 * The test is non-zero rather than positive, so a refund-shaped negative still
 * shows — that is precisely the number somebody needs to see.
 */
export const collectionBreakdown = (s: CollectionSummary): CollectionLine[] => {
  const lines: CollectionLine[] = [
    { label: 'Online', value: s.online_amount_collected || 0 },
    { label: 'Cash', value: s.cash_collected || 0 },
  ];
  const optional: CollectionLine[] = [
    { label: 'Coins', value: s.coins_amount_collected || 0 },
    { label: 'Coupons', value: s.coupons_amount_collected || 0 },
    { label: 'Other', value: s.other_amount_collected || 0 },
  ];
  return lines.concat(optional.filter(l => l.value !== 0));
};

/** Where one lead type of an executive's month sits on the slab ladder. */
export interface ExecMonthSlab {
  units: number;
  slab: string;
  slab_id: string;
  rate: number;
}

/**
 * One row of `executive_incentives` on GET /marketing/orders: an executive's
 * orders and incentive money for one incentive month, over the same filtered
 * set as the header tiles. The slabs are where the month lands once every
 * pending order in it is approved.
 */
export interface ExecutiveIncentiveRow {
  exec_mobile: string;
  exec_name: string;
  exec_role: string;
  month: string;
  orders: number;
  paid_orders: number;
  paid_units: number;
  direct_paid_units: number;
  indirect_paid_units: number;
  paid_amount: number;
  credited_amount: number;
  credited_direct: number;
  credited_indirect: number;
  pending_amount: number;
  pending_direct: number;
  pending_indirect: number;
  pending_orders: number;
  total_incentive: number;
  direct_slab?: ExecMonthSlab;
  indirect_slab?: ExecMonthSlab;
}

/** Stable identity of one executive-month row. */
export const execKey = (r: { exec_mobile: string; month: string }): string => `${r.exec_mobile}:${r.month}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-09' -> 'Sep 2026'; anything else is returned as given. */
export const monthLabel = (month?: string | null): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(month || '');
  if (!m) return month || '—';
  const idx = Number(m[2]) - 1;
  return idx >= 0 && idx < 12 ? `${MONTHS[idx]} ${m[1]}` : (month as string);
};

/**
 * The slab cell for one lead type, or null when the executive had no units
 * of that type in the month — an empty month is not "below first slab".
 */
export const execSlabText = (s?: ExecMonthSlab | null): { units: string; slab: string } | null => {
  if (!s || !s.units) return null;
  // The API's slab label already carries the rate ('6–10 · ₹2,000/unit').
  return { units: `${s.units} ${s.units === 1 ? 'unit' : 'units'}`, slab: s.slab || '—' };
};

/**
 * Whether the caller may approve a whole executive-month at once. Same rule
 * as a single order: a Marketing Manager approves their team but never
 * themselves; a SuperAdmin approves only what a manager onboarded (or their
 * own). Nothing to approve — or a month still below the first slab — hides it.
 */
export const canBulkApprove = (
  row: ExecutiveIncentiveRow,
  caller: { mobile?: string | null; isMarketingManager: boolean; isSuperAdmin: boolean },
): boolean => {
  if (!row.pending_orders || !row.pending_amount) return false;
  const slabs = [row.direct_slab, row.indirect_slab].filter((s) => s && s.units);
  if (slabs.length && slabs.every((s) => isBelowFirstSlab(s!.slab))) return false;
  if (caller.isMarketingManager) return row.exec_mobile !== caller.mobile;
  if (caller.isSuperAdmin) return /manager/i.test(row.exec_role || '') || row.exec_mobile === caller.mobile;
  return false;
};

/**
 * How an order's incentive was worked out: '5 × ₹6,500'. The API prices
 * every order of a month at the exec's present slab rate, so amount / units
 * is that rate. Null when it doesn't divide evenly (a FLAT bonus) or there
 * is nothing to show.
 */
export const incentiveCalcLabel = (units?: number | null, amount?: number | null): string | null => {
  const u = Number(units || 0);
  const a = Number(amount || 0);
  if (u <= 0 || a <= 0 || a % u !== 0) return null;
  return `${u} × ₹${(a / u).toLocaleString('en-IN')}`;
};

/** An incentive plan as GET /incentive-plans lists it (the fields used here). */
export interface IncentivePlanLite {
  plan_id: string;
  name: string;
  status: string;
  period?: string;
  effective_from: string;
  effective_to?: string | null;
  slabs?: { lead_type: string; min_count: number; max_count: number | null; rate: number; payout_type: string; slab_id: string }[];
}

/** One selectable incentive period on My Orders: a plan's date range. */
export interface PlanPeriod {
  plan_id: string;
  name: string;
  /** 'YYYY-MM-DD', inclusive — sent as incentiveFrom / incentiveTo. */
  from: string;
  to: string;
  slabs: NonNullable<IncentivePlanLite['slabs']>;
}

const day = (v?: string | null) => (v || '').slice(0, 10);
const ddmmyyyy = (d: string) => (/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : d);

/**
 * The ACTIVE monthly plans that have started by `today`, newest first — the
 * periods a manager can divide orders and incentives by. Future plans have
 * no orders yet; archived versions are superseded by the ACTIVE one over
 * the same dates. A plan with no end date runs to `today`.
 */
export const planPeriods = (plans: IncentivePlanLite[] | null | undefined, today: string): PlanPeriod[] =>
  (plans || [])
    .filter((p) => p.status === 'ACTIVE' && (!p.period || p.period === 'MONTHLY'))
    .filter((p) => day(p.effective_from) && day(p.effective_from) <= today)
    .map((p) => ({
      plan_id: p.plan_id,
      name: p.name,
      from: day(p.effective_from),
      to: day(p.effective_to) || today,
      slabs: p.slabs || [],
    }))
    .sort((a, b) => b.from.localeCompare(a.from));

/** 'October Plan · 01/10/2026 – 31/10/2026' */
export const planPeriodLabel = (p: { name: string; from: string; to: string }): string =>
  `${p.name} · ${ddmmyyyy(p.from)} – ${ddmmyyyy(p.to)}`;

/** The period covering `today`, else the newest one, else null. */
export const currentPlanPeriod = <T extends { from: string; to: string }>(periods: T[], today: string): T | null =>
  periods.find((p) => p.from <= today && today <= p.to) || periods[0] || null;

/**
 * The months to ask GET /farm-incentive-plans for, as MM-YYYY: this month
 * and the `count - 1` before it, newest first. Never a future month — that
 * endpoint seeds a default plan for any current-or-later month it is asked
 * about, so opening the screen must not reach ahead.
 */
export const recentMonthYears = (today: string, count: number): string[] => {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push(`${String(d.getUTCMonth() + 1).padStart(2, '0')}-${d.getUTCFullYear()}`);
  }
  return out;
};
export interface MarketingOrderRow {
  order_id: string;
  investor_mobile: string;
  investor_name: string;
  numUnits: number;
  // Units with the MOU given / units (the MOU column).
  mou?: { given: number; total: number };
  paymentStatus: string;
  status: string;
  totalCost: number;
  totalPaid: number;
  created_at?: string;
  paidAt?: string;
  lead_id?: string;
  lead_name?: string;
  farmvisit_by?: {
    mobile: string;
    name: string;
    visit_type?: string | null;
    checked_in_at?: string | null;
    checked_out_at?: string | null;
    duration_minutes?: number | null;
  } | null;
  farm_incentive?: {
    status: 'CREDITED' | 'PROJECTED' | 'NOT_ELIGIBLE' | string;
    amount: number;
    units: number;
    credited_to_mobile?: string;
    credited_to_name?: string;
    slab?: string;
    plan_id?: string;
    period_key?: string;
    slab_id?: string;
    reason?: string;
    approved_by?: string;
    approved_by_name?: string;
    approved_at?: string;
    projection_basis?: { sales_before?: number; sales_after?: number };
    slab_breakdown?: { units: number; slab_range: string; rate: number; amount: number; slab_id: string }[];
  } | null;
  // The farm side's OWN approval flag — independent of incentive_approved
  // (the marketing incentive), which the Farm Orders tab does not act on.
  farm_incentive_approved?: boolean;
  farm_incentive_approved_by?: string;
  farm_incentive_approved_by_name?: string;
  farm_incentive_approved_by_role?: string;
  farm_incentive_approved_at?: string;
  lead_mobile_number?: string;
  lead_platform?: string;
  lead_source?: string;
  lead_campaign_name?: string;
  lead_status?: string;
  lead_reference_name?: string;
  lead_reference_number?: string;
  referred_by_name?: string;
  referred_by_mobile?: string;
  referred_by_user_id?: string;
  referred_by_role?: string;
  unit_placed_by_mobile?: string;
  onboarded_by_name?: string;
  onboarded_by_mobile?: string;
  order_onboarded_by_name?: string;
  order_onboarded_by_mobile?: string;
  with_cpf?: boolean;
  cpf_status?: string;
  incentive_approved: boolean;
  incentive_approved_by?: string;
  incentive_approved_by_name?: string;
  incentive_approved_by_role?: string;
  incentive_approved_at?: string;
  placed_at: string;
  order_approved_date?: string;
  lead_type?: string;
  incentive?: {
    lead_type: string;
    status: 'CREDITED' | 'PROJECTED' | 'NOT_ELIGIBLE' | string;
    amount: number;
    units?: number;
    slab: string;
    slab_id?: string;
    credited_to_name: string;
    credited_to_mobile?: string;
    reason: string;
    projection_basis?: any | null;
    calc?: string;
    basis?: string;
  };
}

/** '2026-09' -> '01/09/2026 – 30/09/2026', the month's inclusive date range. */
export const monthDates = (monthStr: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(monthStr || '');
  if (!m) return '';
  const lastDay = new Date(Number(m[1]), Number(m[2]), 0).getDate();
  return `01/${m[2]}/${m[1]} – ${lastDay}/${m[2]}/${m[1]}`;
};

export type ExecIncentiveStatus = 'CREDITED' | 'PENDING_APPROVAL' | 'NONE';

/**
 * Where an executive-month's incentive stands, for the Status column of both
 * executive tables (marketing and farm): anything still awaiting approval
 * makes it Pending approval, even if part of the month is already credited —
 * that is the row an approver still has to act on. Credited once nothing is
 * left pending and something was paid; NONE when there is neither.
 */
export const execIncentiveStatus = (r: {
  pendingOrders: number; creditedOrders: number;
}): ExecIncentiveStatus => {
  if (r.pendingOrders > 0) return 'PENDING_APPROVAL';
  if (r.creditedOrders > 0) return 'CREDITED';
  return 'NONE';
};
